import { getCanonical, getMeta } from 'site-modules/shared/utils/seo-helper';
import { getFullImageUrl, MAX_HEIGHT, MAX_WIDTH } from 'client/utils/image-helpers';
import { getWebpageSchema } from 'client/seo/schemas/webpage';
import { getArticleSchema } from 'client/seo/schemas/article';
import { getImageSchema } from 'client/seo/schemas/image';
import { getVideoSchema } from 'client/seo/schemas/video';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { buildBreadcrumbs } from 'site-modules/shared/utils/top-rated/top-rated';
import { DEFAULT_DESCRIPTION } from 'site-modules/shared/constants/seo';

/**
 * Get simple editorial-focused meta for content
 * @param {Object} content
 */
export const getMetaFromEntry = (content, entryId = 'hero-content') => {
  const entry = content.child(entryId);
  const titleTag = content.child('hero-content').metadata('titleTag').value();
  const title = titleTag ? `${titleTag} | Edmunds` : `${content.title} | Edmunds`;
  const metaDescToUse =
    entry.metadata('metaDescription').value() || content.metadata('metaDescription').value(DEFAULT_DESCRIPTION);
  return {
    ...getMeta(content),
    title,
    description: metaDescToUse,
  };
};

export const getModifiedCanonical = (content, year) => {
  const canonical = getCanonical(content);
  return {
    canonical: year === '2020' ? canonical.replace('best-cars', 'top-rated/2020') : canonical,
  };
};

export function getEntry(content, id) {
  const main = content.child('main');
  const footer = content.child('footer');

  if (main.hasChild(id)) {
    return main.child(id);
  }

  if (footer.hasChild(id)) {
    return footer.child(id);
  }

  return content.child(id);
}

export function getVideoSchemaData(videoEntry) {
  const thumbnailURL = videoEntry.metadata('thumbnailURL').value();
  const videoTranscript = videoEntry.metadata('videoTranscript').value();
  const videoDescription = videoEntry.metadata('videoDescription').value();
  const videoTitle = videoEntry.metadata('videoTitle').value();
  const uploadDate = videoEntry.metadata('uploadDate').value();
  const videoId = videoEntry.metadata('videoId').value();

  return getVideoSchema({
    thumbnailURL,
    videoTranscript,
    videoDescription,
    videoTitle,
    uploadDate,
    videoId,
  });
}

export function getImage(content) {
  const heroContent = content.child('hero-content');
  const image = heroContent.metadata('thumbnailURL').value(heroContent.metadata('ogImage').value());

  const imageUrl = getFullImageUrl(image, MAX_WIDTH);
  return (
    image && {
      url: imageUrl,
      uri: imageUrl,
      width: heroContent.metadata('ogImageWidth').value(`${MAX_WIDTH}`),
      height: heroContent.metadata('ogImageHeight').value(`${MAX_HEIGHT}`),
    }
  );
}

export function getTopRatedIndexSeoObject(content, pathname, year) {
  const meta = getMetaFromEntry(content);
  const image = getImage(content);
  const videoData = getVideoSchemaData(content.child('video'));
  const canonical = getModifiedCanonical(content, year);

  return {
    ...meta,
    ...canonical,
    image,
    jsonld: [
      getBreadcrumbSchema(buildBreadcrumbs(content.child('config'), content.title, pathname)),
      getArticleSchema({ articleObject: content, image: image && getImageSchema(image) }),
      videoData,
    ],
  };
}

export function getTopRatedArticleSeoObject(content, pathname) {
  const meta = getMetaFromEntry(content);
  const image = getImage(content);
  const videoData = getVideoSchemaData(content.child('hero-content'));

  return {
    ...meta,
    image,
    jsonld: [
      getWebpageSchema(meta.title),
      getBreadcrumbSchema(buildBreadcrumbs(content.child('config'), content.title, pathname)),
      getArticleSchema({ articleObject: content, image: image && getImageSchema(image) }),
      videoData,
    ],
  };
}
